import type { Block } from "../content/types";
import { article as bibleKnowledgeQuiz } from "./bible-knowledge-quiz";
import { article as phoneAndFaithTest } from "./phone-and-faith-test";
import { article as prayerStyleQuiz } from "./prayer-style-quiz";
import type { Quiz, QuizOutcome } from "./types";
import { article as whichBibleCharacterAreYou } from "./which-bible-character-are-you";

/**
 * Every quiz, in the order `/quiz` lists them.
 *
 * Static, for the same reason `content/index.ts` is: the hub page needs all of
 * their titles and the sitemap needs all of their dates, so splitting the
 * corpus would cost a request to save nothing. The seam is documented there and
 * applies to both.
 */
export const QUIZZES: Quiz[] = [
  phoneAndFaithTest,
  whichBibleCharacterAreYou,
  bibleKnowledgeQuiz,
  prayerStyleQuiz,
];

export const QUIZZES_BY_SLUG: Record<string, Quiz> = Object.fromEntries(
  QUIZZES.map((q) => [q.slug, q]),
);

export function quizBySlug(slug: string): Quiz | undefined {
  return QUIZZES_BY_SLUG[slug];
}

/** The path a quiz lives at. One place, so the sitemap cannot drift. */
export function quizPath(slug: string): string {
  return `/quiz/${slug}`;
}

/**
 * The result for a set of answers, as `questionId → option index`.
 *
 * Unanswered questions are simply absent, which is deliberate: a reader who
 * has answered four of twelve gets the result those four point at, updated as
 * they go. A quiz that refuses to say anything until the last question is a
 * quiz most people abandon at question seven, and an abandoned quiz is a page
 * nobody shares.
 *
 * Returns `undefined` only when nothing has been answered at all.
 */
export function scoreQuiz(
  quiz: Quiz,
  answers: Record<string, number>,
): QuizOutcome | undefined {
  const answered = quiz.questions.filter((q) => answers[q.id] !== undefined);
  if (answered.length === 0) return undefined;

  if (quiz.kind === "knowledge") {
    const correct = answered.filter(
      (q) => q.options[answers[q.id]]?.correct,
    ).length;
    // The highest band this score reaches. Bands are checked for coverage by
    // the test, so the `?? last` is a type narrowing rather than a fallback.
    return (
      [...quiz.outcomes]
        .sort((a, b) => (b.min ?? 0) - (a.min ?? 0))
        .find((o) => correct >= (o.min ?? 0)) ?? quiz.outcomes.at(-1)
    );
  }

  const totals = new Map<string, number>(quiz.outcomes.map((o) => [o.id, 0]));
  for (const question of answered) {
    const option = question.options[answers[question.id]];
    for (const [id, points] of Object.entries(option?.scores ?? {})) {
      totals.set(id, (totals.get(id) ?? 0) + points);
    }
  }
  // Ties break toward the outcome declared FIRST. Written down because it is a
  // real editorial decision: the outcomes are ordered so that the one a tie
  // should resolve to — the more careful reading of an ambiguous set of
  // answers — comes earlier.
  let best = quiz.outcomes[0];
  for (const outcome of quiz.outcomes) {
    if ((totals.get(outcome.id) ?? 0) > (totals.get(best.id) ?? 0)) {
      best = outcome;
    }
  }
  return best;
}

/** How many questions a reader is signing up for. Printed before they start. */
export function questionCount(quiz: Quiz): number {
  return quiz.questions.length;
}

/**
 * Roughly how long the page takes to read, in minutes.
 *
 * Counts the prose, the questions AND every outcome, because the page really
 * does contain all of them — see `types.ts` for why they are all rendered.
 * 220 words a minute, rounded up, same figure the blog uses.
 */
export function quizMinutes(quiz: Quiz): number {
  const prose = [...quiz.intro, ...quiz.after].reduce(
    (n, block) => n + blockWords(block),
    0,
  );
  const questions = quiz.questions.reduce(
    (n, q) =>
      n +
      count(q.prompt) +
      count(q.because ?? "") +
      q.options.reduce((m, o) => m + count(o.text), 0),
    0,
  );
  const outcomes = quiz.outcomes.reduce(
    (n, o) =>
      n +
      count(o.summary) +
      o.body.reduce((m, p) => m + count(p), 0) +
      o.practice.reduce((m, p) => m + count(p), 0) +
      count(o.verse.text),
    0,
  );
  return Math.max(1, Math.round((prose + questions + outcomes) / 220));
}

function count(text: string): number {
  return text.trim() ? text.trim().split(/\s+/).length : 0;
}

function blockWords(block: Block): number {
  switch (block.type) {
    case "ul":
    case "ol":
      return count(block.items.join(" "));
    case "verse":
      return count(block.verse.text);
    default:
      return count(block.text);
  }
}

/** The `<h2>`s of a quiz's prose, for its table of contents. */
export function quizOutline(quiz: Quiz): { id: string; text: string }[] {
  return [...quiz.intro, ...quiz.after].flatMap((block) =>
    block.type === "h2" ? [{ id: block.id, text: block.text }] : [],
  );
}

export type {
  Quiz,
  QuizKind,
  QuizOption,
  QuizOutcome,
  QuizQuestion,
} from "./types";
